import React, { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { adminListPartners } from '../api';

const PAGE = 50;

const PartnersPage = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [deleted, setDeleted] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-partners', q, page, deleted],
    queryFn: () => adminListPartners({ q, deleted, limit: PAGE, offset: page * PAGE }),
    placeholderData: keepPreviousData,
  });
  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-brand-faint mb-1">Administración</p>
        <h1 className="font-display text-2xl font-semibold text-brand-text tracking-tight">Clientes</h1>
      </div>

      <div className="mb-4 flex gap-1.5">
        {[[false, 'Activos'], [true, 'Eliminados']].map(([v, label]) => (
          <button key={label} onClick={() => { setDeleted(v); setPage(0); }}
            className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] border transition-colors ${
              deleted === v ? 'border-brand-accent text-brand-accent bg-brand-accent/5'
                : 'border-brand-border-md text-brand-faint hover:border-brand-text hover:text-brand-muted'}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="relative mb-5 max-w-sm">
        <Search size={14} className="absolute left-0 top-1/2 -translate-y-1/2 text-brand-faint" />
        <input
          value={q}
          onChange={e => { setQ(e.target.value); setPage(0); }}
          placeholder="Buscar por nombre, teléfono o correo"
          className="w-full bg-transparent border-b border-brand-border-md py-2 pl-6 text-[13px] text-brand-text placeholder-brand-faint outline-none focus:border-brand-accent"
        />
      </div>

      <div className="border border-brand-border overflow-hidden">
        <table className="w-full text-left text-[13px]">
          <thead>
            <tr className="border-b border-brand-border bg-brand-surface">
              {['Nombre', 'Teléfono', 'Correo', 'Pedidos', 'Comprado', 'Último pedido'].map((h, i) => (
                <th key={h} className={`px-4 py-3 font-mono text-[10px] uppercase tracking-[0.14em] text-brand-faint font-normal ${i === 2 || i === 5 ? 'hidden md:table-cell' : ''}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center font-mono text-[11px] text-brand-faint tracking-widest uppercase">Cargando…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-brand-muted text-[13px]">Sin resultados</td></tr>
            ) : items.map(p => (
              <tr key={p.id} onClick={() => navigate(`/admin/clientes/${p.id}`)}
                className="border-b border-brand-border hover:bg-brand-surface/50 transition-colors cursor-pointer">
                <td className="px-4 py-3 text-brand-text">{p.name || <span className="text-brand-faint">Sin nombre</span>}</td>
                <td className="px-4 py-3 font-mono text-[12px] text-brand-muted">{p.phone ? `+${p.phone}` : '—'}</td>
                <td className="px-4 py-3 text-brand-muted text-[12px] hidden md:table-cell">{p.email || '—'}</td>
                <td className="px-4 py-3 text-brand-muted">{p.orders_count}</td>
                <td className="px-4 py-3 text-brand-text">S/ {p.total_spent.toFixed(2)}</td>
                <td className="px-4 py-3 text-brand-muted text-[12px] hidden md:table-cell">
                  {p.last_order_at ? new Date(p.last_order_at).toLocaleDateString('es-PE') : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex items-center justify-between font-mono text-[10px] text-brand-faint">
        <span>{total} clientes</span>
        <div className="flex gap-3">
          <button disabled={page === 0} onClick={() => setPage(p => p - 1)} className="disabled:opacity-30 hover:text-brand-text">← Anterior</button>
          <button disabled={(page + 1) * PAGE >= total} onClick={() => setPage(p => p + 1)} className="disabled:opacity-30 hover:text-brand-text">Siguiente →</button>
        </div>
      </div>
    </div>
  );
};

export default PartnersPage;
